import { useState, useEffect, useRef } from 'react';
import { Zap, Check, RefreshCw, BarChart3, LogOut, Package, TrendingUp, DollarSign, AlertCircle, X, Clock, Shield, Play, Sparkles, Target, Brain, Lightbulb, Mail } from 'lucide-react';
import { supabase } from './lib/supabaseClient';
import AdminPanel from './components/AdminPanel';
import ProductDashboard from './components/ProductDashboard';

// ============================================
// VIDEO MODAL COMPONENT
// ============================================
function VideoModal({ isOpen, onClose }) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={onClose}>
      <div className="relative w-full max-w-4xl" onClick={e => e.stopPropagation()}>
        <button
          onClick={onClose}
          className="absolute -top-12 right-0 text-white/70 hover:text-white transition flex items-center gap-2"
        >
          <X className="w-6 h-6" />
          Close
        </button>
        <div className="rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
          <video
            controls
            autoPlay
            className="w-full aspect-video bg-black"
            src="/videos/OVERVIEW VIDEO.mp4"
          >
            Your browser does not support the video tag.
          </video>
        </div>
      </div>
    </div>
  );
}

const ADMIN_EMAIL = 'arealhuman21@gmail.com';

// ============================================
// NAVBAR COMPONENT - Sticky Glassmorphism
// ============================================
function Navbar({ onJoinWaitlist }) {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'glass-navbar' : 'bg-transparent'}`}>
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-emerald-500/20 rounded-lg">
            <Zap className="w-5 h-5 text-emerald-400" />
          </div>
          <span className="text-white font-bold text-xl tracking-tight">AutoMerchant</span>
        </div>
        <button
          onClick={onJoinWaitlist}
          className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 rounded-lg text-white text-sm font-medium transition-all"
        >
          Get Started
        </button>
      </div>
    </nav>
  );
}

// ============================================
// FLOATING ANIMATED SYMBOLS
// ============================================
function FloatingSymbols() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Dollar signs */}
      <div className="absolute top-1/4 left-[10%] text-emerald-500/20 text-4xl animate-float-slow">$</div>
      <div className="absolute top-1/3 right-[15%] text-emerald-500/15 text-5xl animate-float-medium">$</div>
      <div className="absolute bottom-1/4 left-[20%] text-emerald-500/10 text-3xl animate-float-fast">$</div>

      {/* Percentage signs */}
      <div className="absolute top-[20%] right-[25%] text-cyan-500/20 text-3xl animate-float-medium">%</div>
      <div className="absolute bottom-[30%] right-[10%] text-cyan-500/15 text-4xl animate-float-slow">%</div>
      <div className="absolute top-[40%] left-[5%] text-cyan-500/10 text-2xl animate-float-fast">%</div>

      {/* Up arrows / trend symbols */}
      <div className="absolute top-[15%] left-[30%] text-green-500/20 text-3xl animate-float-fast">↑</div>
      <div className="absolute bottom-[20%] right-[30%] text-green-500/15 text-4xl animate-float-slow">↗</div>

      {/* Stars/sparkles */}
      <div className="absolute top-[25%] right-[8%] text-yellow-500/20 text-2xl animate-pulse-slow">✦</div>
      <div className="absolute bottom-[35%] left-[12%] text-yellow-500/15 text-3xl animate-pulse-medium">✦</div>
      <div className="absolute top-[45%] right-[20%] text-purple-500/20 text-2xl animate-pulse-fast">✧</div>

      {/* Chart symbols */}
      <div className="absolute bottom-[15%] left-[35%] text-blue-500/15 text-3xl animate-float-medium">📊</div>
      <div className="absolute top-[10%] right-[35%] text-emerald-500/10 text-2xl animate-float-slow">📈</div>
    </div>
  );
}

// ============================================
// HERO SECTION - Linear.app Style
// ============================================
function HeroSection({ onJoinWaitlist, onWatchDemo }) {
  return (
    <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden pt-16">
      {/* Background radial glow */}
      <div className="absolute inset-0 bg-radial-glow" />

      {/* Floating animated symbols */}
      <FloatingSymbols />

      <div className="relative z-10 text-center max-w-4xl mx-auto px-6 py-20">
        {/* Badge Pills */}
        <div className="flex flex-wrap justify-center gap-3 mb-8 animate-fade-in-up">
          <div className="pill-badge">
            <Sparkles className="w-4 h-4" />
            <span>AI-Powered Pricing for Shopify</span>
          </div>
          <div className="pill-badge border-amber-500/30 bg-amber-500/10">
            <Clock className="w-4 h-4 text-amber-400" />
            <span className="text-amber-300">Waitlist Open • Manual Onboarding</span>
          </div>
        </div>

        {/* Main Headline */}
        <h1 className="text-5xl md:text-7xl font-bold text-white text-headline mb-6 animate-fade-in-up" style={{animationDelay: '0.1s'}}>
          Pricing That Actually
          <span className="block text-gradient-primary mt-2">Explains Itself</span>
        </h1>

        {/* Subtext */}
        <p className="text-xl text-slate-400 max-w-2xl mx-auto mb-10 text-subheadline animate-fade-in-up" style={{animationDelay: '0.2s'}}>
          Stop guessing. Every AI recommendation shows you the exact reasoning.
          No black boxes. No blind trust. Just transparent pricing optimization.
        </p>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12 animate-fade-in-up" style={{animationDelay: '0.3s'}}>
          <button
            onClick={onJoinWaitlist}
            className="btn-primary-glow px-8 py-4 text-lg flex items-center justify-center gap-2"
          >
            <Zap className="w-5 h-5" />
            Get Started Free
          </button>
          <button
            onClick={onWatchDemo}
            className="btn-ghost px-8 py-4 text-lg flex items-center justify-center gap-2"
          >
            <Play className="w-5 h-5" />
            Watch Demo
          </button>
        </div>

        {/* Social Proof */}
        <div className="flex flex-wrap items-center justify-center gap-6 text-slate-500 text-sm animate-fade-in-up" style={{animationDelay: '0.4s'}}>
          <span className="flex items-center gap-2">
            <Check className="w-4 h-4 text-emerald-500" />
            5-minute setup
          </span>
          <span className="flex items-center gap-2">
            <Check className="w-4 h-4 text-emerald-500" />
            100% transparent AI
          </span>
          <span className="flex items-center gap-2">
            <Check className="w-4 h-4 text-amber-500" />
            Manual onboarding by founder
          </span>
        </div>
      </div>
    </section>
  );
}

// ============================================
// BENTO GRID FEATURE SECTION
// ============================================
function BentoFeatureGrid() {
  return (
    <section className="py-24 px-6 relative">
      <div className="absolute inset-0 bg-radial-glow-subtle" />

      <div className="max-w-6xl mx-auto relative z-10">
        <h2 className="text-4xl md:text-5xl font-bold text-white text-center mb-4 text-headline">
          Why Merchants Choose Us
        </h2>
        <p className="text-slate-400 text-center mb-16 max-w-2xl mx-auto text-lg">
          The only pricing tool that shows you exactly why it makes every recommendation
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Large Feature Card - AI Reasoning (spans 2 cols and 2 rows) */}
          <div className="md:col-span-2 md:row-span-2 glass-card-linear rounded-2xl p-8 border-glow-hover bento-item">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-emerald-500/20 rounded-lg">
                <Sparkles className="w-6 h-6 text-emerald-400" />
              </div>
              <span className="text-emerald-400 font-medium text-sm">Core Feature</span>
            </div>
            <h3 className="text-2xl font-bold text-white mb-3">Full AI Reasoning</h3>
            <p className="text-slate-400 mb-6">
              See exactly why every price change is recommended. No more "trust the algorithm" — understand the math, the market signals, and the logic.
            </p>
            {/* Mini demo preview */}
            <div className="bg-dark-950 rounded-xl p-5 border border-white/5">
              <div className="flex items-center justify-between mb-3">
                <span className="text-white font-medium">Premium Headphones</span>
                <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded-full">Low Margin</span>
              </div>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div className="bg-white/5 rounded-lg p-3">
                  <p className="text-slate-500 text-xs">Current</p>
                  <p className="text-white text-xl font-bold">$89.99</p>
                </div>
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3">
                  <p className="text-slate-500 text-xs">Recommended</p>
                  <p className="text-emerald-400 text-xl font-bold">$119.99</p>
                </div>
              </div>
              <div className="bg-white/5 rounded-lg p-3">
                <p className="text-slate-500 text-xs mb-1">AI Reasoning:</p>
                <p className="text-emerald-300 text-sm font-mono">
                  "Margin 25% is below 30% target. Sales velocity (12/wk) shows room for increase. Confidence: 87%"
                </p>
              </div>
            </div>
          </div>

          {/* Feature Card - Setup Speed */}
          <div className="glass-card-linear rounded-2xl p-6 border-glow-hover bento-item">
            <div className="p-2 bg-cyan-500/20 rounded-lg w-fit mb-4">
              <Clock className="w-6 h-6 text-cyan-400" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">5-Minute Setup</h3>
            <p className="text-slate-400 text-sm">
              Connect Shopify, set your costs, done. No complex rules or competitor tracking.
            </p>
          </div>

          {/* Feature Card - Manual Control */}
          <div className="glass-card-linear rounded-2xl p-6 border-glow-hover bento-item">
            <div className="p-2 bg-purple-500/20 rounded-lg w-fit mb-4">
              <Shield className="w-6 h-6 text-purple-400" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">You Stay in Control</h3>
            <p className="text-slate-400 text-sm">
              Every recommendation needs your approval. Nothing changes without your say.
            </p>
          </div>

          {/* Feature Card - Margin Protection */}
          <div className="glass-card-linear rounded-2xl p-6 border-glow-hover bento-item">
            <div className="p-2 bg-green-500/20 rounded-lg w-fit mb-4">
              <TrendingUp className="w-6 h-6 text-green-400" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Margin Protection</h3>
            <p className="text-slate-400 text-sm">
              AI ensures you never sell below your target margin. Protect your profits automatically.
            </p>
          </div>

          {/* Feature Card - Auto Analysis */}
          <div className="glass-card-linear rounded-2xl p-6 border-glow-hover bento-item">
            <div className="p-2 bg-orange-500/20 rounded-lg w-fit mb-4">
              <RefreshCw className="w-6 h-6 text-orange-400" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Auto Analysis</h3>
            <p className="text-slate-400 text-sm">
              AI analyzes your products every 30 minutes. Always up-to-date recommendations.
            </p>
          </div>

          {/* Feature Card - Real-time Stats */}
          <div className="glass-card-linear rounded-2xl p-6 border-glow-hover bento-item">
            <div className="p-2 bg-blue-500/20 rounded-lg w-fit mb-4">
              <BarChart3 className="w-6 h-6 text-blue-400" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Real-time Stats</h3>
            <p className="text-slate-400 text-sm">
              See your profit potential, margin improvements, and ROI at a glance.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================
// OLD WAY VS NEW WAY COMPARISON
// ============================================
function ComparisonSection() {
  return (
    <section className="py-24 px-6">
      <div className="max-w-6xl mx-auto">
        <h2 className="text-4xl md:text-5xl font-bold text-white text-center mb-4 text-headline">
          The Difference is Clear
        </h2>
        <p className="text-slate-400 text-center mb-16 max-w-2xl mx-auto text-lg">
          Stop gambling with black-box pricing tools
        </p>

        <div className="grid md:grid-cols-2 gap-6">
          {/* OLD WAY - Left Side */}
          <div className="relative rounded-2xl p-8 bg-gradient-to-br from-red-950/30 to-red-900/10 border border-red-500/20">
            <div className="absolute top-4 right-4 px-3 py-1 bg-red-500/20 border border-red-500/30 rounded-full">
              <span className="text-red-400 text-sm font-medium">The Old Way</span>
            </div>

            <h3 className="text-2xl font-bold text-red-200 mb-6 mt-4">Other Pricing Tools</h3>

            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <X className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                <span className="text-slate-300">Black box AI — no explanation why</span>
              </li>
              <li className="flex items-start gap-3">
                <X className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                <span className="text-slate-300">Hours of complex configuration</span>
              </li>
              <li className="flex items-start gap-3">
                <X className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                <span className="text-slate-300">Auto-applies changes without asking</span>
              </li>
              <li className="flex items-start gap-3">
                <X className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                <span className="text-slate-300">Requires competitor URL tracking</span>
              </li>
              <li className="flex items-start gap-3">
                <X className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                <span className="text-slate-300">Trust it blindly or don't use it</span>
              </li>
            </ul>
          </div>

          {/* NEW WAY - Right Side */}
          <div className="relative rounded-2xl p-8 bg-gradient-to-br from-emerald-950/30 to-emerald-900/10 border border-emerald-500/30 shadow-glow-sm">
            <div className="absolute top-4 right-4 px-3 py-1 bg-emerald-500/20 border border-emerald-500/30 rounded-full animate-border-glow">
              <span className="text-emerald-400 text-sm font-medium">AutoMerchant</span>
            </div>

            <h3 className="text-2xl font-bold text-emerald-200 mb-6 mt-4">The Transparent Way</h3>

            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                <span className="text-slate-300">Full reasoning for every recommendation</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                <span className="text-slate-300">5-minute setup, zero configuration</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                <span className="text-slate-300">You approve every single change</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                <span className="text-slate-300">No competitor tracking needed</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                <span className="text-slate-300">Understand it, trust it, profit from it</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================
// INTERACTIVE DEMOS SECTION (Restyled)
// ============================================
function InteractiveRecommendationCard() {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className="glass-card-linear rounded-xl p-6 border-glow-hover"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-white">Premium Wireless Headphones</h3>
          <p className="text-sm text-slate-500">SKU: WH-1000XM4</p>
        </div>
        <span className="px-3 py-1 bg-red-500/20 border border-red-500/30 rounded-full text-red-300 text-xs font-bold">
          CRITICAL
        </span>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="bg-dark-950 rounded-lg p-3 border border-white/5">
          <p className="text-slate-500 text-xs mb-1">Current Price</p>
          <p className="text-white text-2xl font-bold">$89.99</p>
          <p className="text-red-400 text-sm">25% margin</p>
        </div>
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3">
          <p className="text-slate-500 text-xs mb-1">AI Recommended</p>
          <p className="text-emerald-300 text-2xl font-bold">$119.99</p>
          <p className="text-emerald-400 text-sm">40% margin</p>
        </div>
      </div>

      <div className={`bg-dark-950 border ${isHovered ? 'border-emerald-500/30' : 'border-white/5'} rounded-lg p-4 transition-colors duration-300`}>
        <p className="text-slate-500 text-xs mb-2">AI Reasoning:</p>
        <p className="text-emerald-300 font-mono text-sm leading-relaxed">
          "Margin 25% is below healthy minimum of 30%. Raising to $119.99 (+33%) for 40% target margin. Sales velocity: 12 units/week. Confidence: 87%"
        </p>
      </div>

      <div className="flex gap-3 mt-4">
        <button className="flex-1 py-2.5 btn-primary-glow flex items-center justify-center gap-2 text-sm">
          <Check className="w-4 h-4" />
          Apply +$360/mo
        </button>
        <button className="px-4 py-2.5 btn-ghost text-sm">
          Reject
        </button>
      </div>

      {isHovered && (
        <p className="text-center text-emerald-400 text-xs mt-3 animate-fade-in-up">
          This is what you see for EVERY recommendation
        </p>
      )}
    </div>
  );
}

function InteractiveStatsDashboard() {
  const [activeTab, setActiveTab] = useState(0);
  const stats = [
    { label: 'Total Products', value: '47', change: '+12%', icon: Package, color: 'blue' },
    { label: 'Avg Margin', value: '38%', change: '+5%', icon: TrendingUp, color: 'green' },
    { label: 'Monthly Revenue', value: '$23.4K', change: '+18%', icon: DollarSign, color: 'purple' },
    { label: 'Recommendations', value: '8', change: 'new', icon: AlertCircle, color: 'orange' }
  ];

  const colorClasses = {
    blue: 'bg-blue-500/20 border-blue-500/30 text-blue-400',
    green: 'bg-green-500/20 border-green-500/30 text-green-400',
    purple: 'bg-purple-500/20 border-purple-500/30 text-purple-400',
    orange: 'bg-orange-500/20 border-orange-500/30 text-orange-400'
  };

  return (
    <div className="glass-card-linear rounded-xl p-6 border-glow-hover">
      <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
        <BarChart3 className="w-6 h-6 text-emerald-400" />
        Your Dashboard Overview
      </h3>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {stats.map((stat, idx) => {
          const Icon = stat.icon;
          const isActive = activeTab === idx;

          return (
            <div
              key={idx}
              className={`${colorClasses[stat.color]} border rounded-lg p-4 cursor-pointer transition-all duration-300 ${isActive ? 'scale-105 shadow-lg' : 'hover:scale-102'}`}
              onClick={() => setActiveTab(idx)}
            >
              <div className="flex items-center justify-between mb-2">
                <Icon className="w-5 h-5" />
                <span className="text-emerald-400 text-xs font-bold">{stat.change}</span>
              </div>
              <p className="text-2xl font-bold text-white">{stat.value}</p>
              <p className="text-slate-400 text-xs mt-1">{stat.label}</p>
            </div>
          );
        })}
      </div>

      <p className="text-center text-emerald-400 text-sm mt-4">
        Click the cards to see them animate
      </p>
    </div>
  );
}

function InteractiveROICalculator() {
  const [profit, setProfit] = useState(450);

  return (
    <div className="glass-card-linear rounded-xl p-6 border-glow-hover">
      <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
        <DollarSign className="w-6 h-6 text-green-400" />
        Potential Monthly Profit Increase
      </h3>

      <div className="text-center mb-6">
        <p className="text-5xl font-black text-emerald-400 mb-2">
          +${profit.toLocaleString()}/mo
        </p>
        <p className="text-slate-400">If you apply all current recommendations</p>
      </div>

      <div className="space-y-3">
        <div className="bg-dark-950 rounded-lg p-4 border border-white/5">
          <div className="flex justify-between items-center mb-2">
            <span className="text-slate-400 text-sm">8 Recommendations Available</span>
            <span className="text-emerald-400 font-bold">+$450</span>
          </div>
          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-full" style={{width: '75%'}}></div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="bg-dark-950 rounded-lg p-3 border border-white/5">
            <p className="text-2xl font-bold text-white">12</p>
            <p className="text-slate-500 text-xs">Products</p>
          </div>
          <div className="bg-dark-950 rounded-lg p-3 border border-white/5">
            <p className="text-2xl font-bold text-emerald-400">+15%</p>
            <p className="text-slate-500 text-xs">Avg Increase</p>
          </div>
          <div className="bg-dark-950 rounded-lg p-3 border border-white/5">
            <p className="text-2xl font-bold text-cyan-400">87%</p>
            <p className="text-slate-500 text-xs">Confidence</p>
          </div>
        </div>
      </div>

      <input
        type="range"
        min="200"
        max="800"
        value={profit}
        onChange={(e) => setProfit(parseInt(e.target.value))}
        className="w-full mt-4 accent-emerald-500"
      />
      <p className="text-center text-emerald-400 text-xs mt-2">
        Drag to see different scenarios
      </p>
    </div>
  );
}

function InteractiveDemosSection() {
  return (
    <section className="py-24 px-6">
      <div className="max-w-6xl mx-auto">
        <h2 className="text-4xl md:text-5xl font-bold text-white text-center mb-4 text-headline">
          Try The Dashboard
        </h2>
        <p className="text-xl text-slate-400 text-center mb-4">
          These are <span className="text-emerald-400 font-medium">real interactive examples</span> of what you'll see
        </p>
        <p className="text-lg text-emerald-400 text-center mb-12 font-medium">
          Hover, click, and drag to explore
        </p>

        <div className="grid md:grid-cols-2 gap-8 mb-8">
          <div>
            <h3 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
              <Check className="w-5 h-5 text-emerald-400" /> Recommendation Card
            </h3>
            <p className="text-slate-400 mb-4 text-sm">Hover to see the AI reasoning in action</p>
            <InteractiveRecommendationCard />
          </div>
          <div>
            <h3 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-emerald-400" /> ROI Calculator
            </h3>
            <p className="text-slate-400 mb-4 text-sm">Drag the slider to see profit projections</p>
            <InteractiveROICalculator />
          </div>
        </div>

        <div>
          <h3 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-purple-400" /> Dashboard Stats
          </h3>
          <p className="text-slate-400 mb-4 text-sm">Click each stat card to see it highlight</p>
          <InteractiveStatsDashboard />
        </div>
      </div>
    </section>
  );
}

// ============================================
// CTA SECTION
// ============================================
function CTASection({ onJoinWaitlist }) {
  return (
    <section className="py-24 px-6">
      <div className="max-w-3xl mx-auto text-center">
        <div className="glass-card-linear rounded-3xl p-12 border-glow-hover">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 text-headline">
            Ready to Stop Guessing?
          </h2>
          <p className="text-xl text-slate-400 mb-2">
            Get manually onboarded in <span className="text-emerald-400 font-bold">5-10 minutes</span>
          </p>
          <p className="text-lg text-emerald-400 mb-10">
            Start seeing <span className="font-bold">transparent AI recommendations</span> today
          </p>
          <button
            onClick={onJoinWaitlist}
            className="btn-primary-glow px-12 py-5 text-xl flex items-center gap-3 mx-auto animate-glow"
          >
            <Zap className="w-6 h-6" />
            Get Started Now
          </button>
          <p className="text-slate-500 text-sm mt-6">
            Sign in with Google to get started
          </p>
        </div>
      </div>
    </section>
  );
}

// ============================================
// FOOTER
// ============================================
function Footer() {
  return (
    <footer className="border-t border-white/5 py-12 px-6">
      <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-emerald-500/20 rounded-lg">
            <Zap className="w-4 h-4 text-emerald-400" />
          </div>
          <span className="text-white font-semibold">AutoMerchant</span>
        </div>
        <div className="flex gap-8 text-slate-500 text-sm">
          <a
            href="mailto:waitlisteremail@gmail.com?subject=Privacy%20Policy%20Inquiry"
            className="hover:text-white transition flex items-center gap-1"
          >
            <Shield className="w-3 h-3" />
            Privacy
          </a>
          <a
            href="mailto:waitlisteremail@gmail.com?subject=Terms%20of%20Service%20Inquiry"
            className="hover:text-white transition"
          >
            Terms
          </a>
          <a
            href="mailto:waitlisteremail@gmail.com"
            className="hover:text-white transition flex items-center gap-1"
          >
            <Mail className="w-3 h-3" />
            Contact
          </a>
        </div>
        <p className="text-slate-600 text-sm">AutoMerchant 2025</p>
      </div>
    </footer>
  );
}

// ============================================
// SUCCESS PAGE - Redesigned
// ============================================
function SuccessPage({ signupNumber, onLogout, userEmail }) {
  useEffect(() => {
    const saveOnboardingPreference = async () => {
      try {
        const API_URL = process.env.REACT_APP_API_URL || '';
        await fetch(`${API_URL}/api/set-manual-onboarding`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: userEmail })
        });
      } catch (error) {
        // Silent fail - non-critical
      }
    };
    saveOnboardingPreference();
  }, [userEmail]);

  const handleLogout = async () => {
    try {
      if (supabase) {
        await supabase.auth.signOut();
      }
      localStorage.removeItem('authToken');
      localStorage.removeItem('wantsManualOnboarding');
      if (onLogout) {
        onLogout();
      }
      window.location.href = '/';
    } catch (err) {
      console.error('Logout error:', err);
      localStorage.removeItem('authToken');
      localStorage.removeItem('wantsManualOnboarding');
      window.location.href = '/';
    }
  };

  return (
    <div className="min-h-screen bg-dark-950 relative overflow-hidden">
      {/* Background radial glow - centered */}
      <div className="absolute inset-0 bg-radial-glow-center" />

      <div className="relative z-10 flex flex-col items-center justify-center min-h-screen p-6">
        {/* Logout button */}
        <button
          onClick={handleLogout}
          className="absolute top-6 right-6 px-4 py-2 btn-ghost text-sm flex items-center gap-2"
        >
          <LogOut className="w-4 h-4" />
          Log Out
        </button>

        {/* Success Icon with glow */}
        <div className="relative mb-8 animate-fade-in-up">
          <div className="absolute inset-0 bg-emerald-500/30 blur-2xl rounded-full scale-150" />
          <div className="relative p-6 bg-emerald-500/20 border border-emerald-500/30 rounded-full">
            <Check className="w-12 h-12 text-emerald-400" />
          </div>
        </div>

        {/* Heading */}
        <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 text-headline text-center animate-fade-in-up" style={{animationDelay: '0.1s'}}>
          You're on the list!
        </h1>

        <p className="text-xl text-slate-400 mb-6 text-center animate-fade-in-up" style={{animationDelay: '0.15s'}}>
          You're signed up for <span className="text-emerald-400 font-bold">AutoMerchant</span>
        </p>

        {/* Signup number badge */}
        {signupNumber && (
          <div className="pill-badge mb-8 animate-fade-in-up" style={{animationDelay: '0.2s'}}>
            <span className="text-emerald-300 text-lg font-bold">Signup #{signupNumber}</span>
          </div>
        )}

        {userEmail && userEmail !== 'benjamincao98@gmail.com' && (
          <p className="text-slate-500 text-sm mb-8 animate-fade-in-up" style={{animationDelay: '0.25s'}}>
            Signed in as: <span className="text-emerald-400">{userEmail}</span>
          </p>
        )}

        {/* Contact cards */}
        <div className="max-w-lg w-full space-y-4 animate-fade-in-up" style={{animationDelay: '0.3s'}}>
          {/* Manual Onboarding Notice */}
          <div className="glass-card-linear rounded-xl p-6 border border-emerald-500/30">
            <h2 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-emerald-400" />
              Manual Onboarding Required
            </h2>
            <p className="text-slate-400 text-sm">
              Contact us to complete setup and start using AutoMerchant
            </p>
          </div>

          {/* Discord Contact */}
          <div className="glass-card-linear rounded-xl p-6 border-glow-hover group">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-indigo-500/20 rounded-lg group-hover:bg-indigo-500/30 transition">
                <Zap className="w-5 h-5 text-indigo-400" />
              </div>
              <h3 className="text-lg font-bold text-white">Discord</h3>
            </div>
            <p className="text-indigo-300 font-mono bg-dark-950 px-4 py-2 rounded-lg inline-block border border-white/5">
              automerchantai_88517
            </p>
            <p className="text-slate-500 text-sm mt-2">Friend us and we'll accept you quickly!</p>
          </div>

          {/* Email Contact */}
          <div className="glass-card-linear rounded-xl p-6 border-glow-hover group">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-pink-500/20 rounded-lg group-hover:bg-pink-500/30 transition">
                <BarChart3 className="w-5 h-5 text-pink-400" />
              </div>
              <h3 className="text-lg font-bold text-white">Email Support</h3>
            </div>
            <a
              href="mailto:waitlisteremail@gmail.com"
              className="text-pink-300 font-mono bg-dark-950 px-4 py-2 rounded-lg inline-block hover:bg-white/5 transition border border-white/5"
            >
              waitlisteremail@gmail.com
            </a>
            <p className="text-slate-500 text-sm mt-2">We respond within hours!</p>
          </div>

          {/* Fast Onboarding */}
          <div className="glass-card-linear rounded-xl p-6 border border-emerald-500/30 bg-emerald-500/5">
            <div className="flex items-center gap-3 mb-2">
              <span className="text-2xl">⚡</span>
              <h3 className="text-lg font-bold text-white">FAST ONBOARDING</h3>
            </div>
            <p className="text-emerald-300 font-medium">
              Process takes 5-10 minutes (max 20)
            </p>
            <p className="text-slate-400 text-sm mt-1">
              Message us and we'll get you set up ASAP!
            </p>
          </div>
        </div>

        {/* What to expect cards */}
        <div className="grid md:grid-cols-2 gap-4 max-w-lg w-full mt-12 animate-fade-in-up" style={{animationDelay: '0.4s'}}>
          <div className="glass-card-linear rounded-xl p-5 border-glow-hover">
            <div className="p-2 bg-purple-500/20 rounded-lg w-fit mb-3">
              <BarChart3 className="w-5 h-5 text-purple-400" />
            </div>
            <h3 className="text-white font-bold mb-1">Smart AI Analysis</h3>
            <p className="text-slate-500 text-sm">Auto pricing optimization every 30 min</p>
          </div>

          <div className="glass-card-linear rounded-xl p-5 border-glow-hover">
            <div className="p-2 bg-green-500/20 rounded-lg w-fit mb-3">
              <RefreshCw className="w-5 h-5 text-green-400" />
            </div>
            <h3 className="text-white font-bold mb-1">Manual Control</h3>
            <p className="text-slate-500 text-sm">Run analysis anytime, 10 daily runs</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// LANDING PAGE - Complete
// ============================================
function LandingPage({ onJoinWaitlist, waitlistCount, userAlreadySignedUp }) {
  const [showVideoModal, setShowVideoModal] = useState(false);

  return (
    <div className="min-h-screen bg-dark-950 relative overflow-hidden">
      <VideoModal isOpen={showVideoModal} onClose={() => setShowVideoModal(false)} />
      <Navbar onJoinWaitlist={onJoinWaitlist} />
      <HeroSection onJoinWaitlist={onJoinWaitlist} onWatchDemo={() => setShowVideoModal(true)} />
      <BentoFeatureGrid />
      <ComparisonSection />
      <InteractiveDemosSection />
      <CTASection onJoinWaitlist={onJoinWaitlist} />
      <Footer />
    </div>
  );
}

// ============================================
// MAIN APP
// ============================================
function App() {
  const [view, setView] = useState('landing');
  const [waitlistCount, setWaitlistCount] = useState(null);
  const [signupNumber, setSignupNumber] = useState(null);
  const [userAlreadySignedUp, setUserAlreadySignedUp] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [userEmail, setUserEmail] = useState(null);
  const [userApproved, setUserApproved] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchWaitlistCount();
    const timer = setTimeout(() => {
      fetchWaitlistCount();
    }, 1000);

    const hasOAuthHash = window.location.hash.includes('access_token');
    if (!hasOAuthHash) {
      checkIfUserSignedUp();
    }

    return () => clearTimeout(timer);
  }, []);

  const checkBackendApprovalStatus = async (email, retryCount = 0) => {
    const MAX_RETRIES = 3;
    try {
      const API_URL = process.env.REACT_APP_API_URL || '';

      const response = await fetch(`${API_URL}/api/check-approval`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      if (!response.ok) {
        if (response.status >= 500 && retryCount < MAX_RETRIES) {
          const delay = 1000 * Math.pow(2, retryCount);
          await new Promise(resolve => setTimeout(resolve, delay));
          return checkBackendApprovalStatus(email, retryCount + 1);
        }
        throw new Error(`Failed to check approval status: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.approved && data.token) {
        localStorage.setItem('authToken', data.token);
        setUserApproved(true);
        setView('product');
      } else if (data.suspended) {
        setUserAlreadySignedUp(true);
        setUserApproved(false);
        setView('success');
      } else {
        setUserAlreadySignedUp(true);
        setUserApproved(false);
        setView('success');
        if (data.wantsManualOnboarding) {
          localStorage.setItem('wantsManualOnboarding', 'true');
        }
      }

      window.history.replaceState({}, document.title, window.location.pathname);
    } catch (err) {
      console.error('Backend approval check failed:', err);
      setError({
        message: 'Unable to connect to our servers. Please check your internet connection and try again.',
        canRetry: true,
        retryAction: () => {
          setError(null);
          checkBackendApprovalStatus(email, 0);
        }
      });
      setView('error');
    }
  };

  useEffect(() => {
    if (!supabase) return;

    let hasProcessed = false;

    const { data: authListener } = supabase.auth.onAuthStateChange(async (event, session) => {

      if (event === 'SIGNED_IN' && session && !hasProcessed) {
        const hasHashToken = window.location.hash.includes('access_token');
        const urlParams = new URLSearchParams(window.location.search);
        const hasCodeParam = urlParams.get('code');
        const isOAuthCallback = hasHashToken || hasCodeParam;

        if (isOAuthCallback) {
          hasProcessed = true;
          setIsProcessing(true);

          try {
            setUserEmail(session.user.email);

            if (session.user.email === ADMIN_EMAIL) {
              setView('success');
              setIsProcessing(false);
              window.history.replaceState({}, document.title, window.location.pathname);
              return;
            }

            const userEmail = session.user.email.toLowerCase();

            const { data: existing } = await supabase
              .from('waitlist_emails')
              .select('email, created_at')
              .eq('email', userEmail)
              .maybeSingle();

            if (existing) {
              const { count } = await supabase
                .from('waitlist_emails')
                .select('*', { count: 'exact', head: true })
                .lte('created_at', existing.created_at);
              setSignupNumber(count || 1);
            } else {
              const { error: insertError } = await supabase
                .from('waitlist_emails')
                .insert([{ email: userEmail }])
                .select();

              if (insertError && insertError.code !== '23505') {
                 throw new Error(insertError.message || 'Failed to add to waitlist');
              }
              await supabase.rpc('increment_waitlist');
              const { count } = await supabase.from('waitlist_emails').select('*', { count: 'exact', head: true });
              setSignupNumber(count || 1);
            }

            try {
              const { error: userInsertError } = await supabase
                .from('users')
                .insert([{
                  email: userEmail,
                  approved: false,
                  created_at: new Date().toISOString()
                }])
                .select();

              if (userInsertError && userInsertError.code !== '23505') {
                // Non-critical, continue
              }
            } catch (userErr) {
              // Non-critical
            }
            window.history.replaceState({}, document.title, window.location.pathname);
            setView('success');
            setIsProcessing(false);
          } catch (err) {
            console.error('Signup error:', err);
            setError({
              message: 'A problem occurred during signup. Please refresh and try again.',
              canRetry: false
            });
            setView('error');
            setIsProcessing(false);
          }
        } else {
          const { data: { user } } = await supabase.auth.getUser();
          if (user?.email) {
            setUserEmail(user.email);
            if (user.email === ADMIN_EMAIL) {
              window.history.replaceState({}, document.title, window.location.pathname);
              setView('success');
              return;
            }

            try {
              const userEmail = user.email.toLowerCase();

              const { data: existingUser } = await supabase
                .from('users')
                .select('id')
                .eq('email', userEmail)
                .maybeSingle();

              if (!existingUser) {
                await supabase
                  .from('users')
                  .insert([{
                    email: userEmail,
                    approved: false,
                    created_at: new Date().toISOString()
                  }])
                  .select();
              }
            } catch (userErr) {
              // Non-critical
            }

            await checkBackendApprovalStatus(user.email);
          }
        }
      }
    });

    return () => {
      authListener?.subscription?.unsubscribe();
    };
  }, []);

  const fetchWaitlistCount = async () => {
    const cached = localStorage.getItem('waitlistCount');
    if (cached) {
      setWaitlistCount(parseInt(cached));
    } else {
      setWaitlistCount(7);
    }
    if (!supabase) return;
    try {
      const { count, error } = await supabase.from('waitlist_emails').select('*', { count: 'exact', head: true });
      if (!error && count !== null) {
        setWaitlistCount(count);
        localStorage.setItem('waitlistCount', count.toString());
      }
    } catch (err) {
      // Silent fail
    }
  };

  const checkIfUserSignedUp = async () => {
    if (!supabase) return;
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user?.email) {
        setUserEmail(user.email);
        if (user.email === ADMIN_EMAIL) {
          setView('success');
          return;
        }
        const { data } = await supabase.from('waitlist_emails').select('email, created_at').eq('email', user.email.toLowerCase()).maybeSingle();
        if (data) {
          const { count } = await supabase.from('waitlist_emails').select('*', { count: 'exact', head: true }).lte('created_at', data.created_at);
          setSignupNumber(count);
          setView('success');
        }
      }
    } catch (err) {
      // Silent fail
    }
  };

  const handleJoinWaitlist = async () => {
    if (!supabase) {
      alert('Supabase is not configured. Please contact support.');
      return;
    }
    try {
      const redirectUrl = process.env.NODE_ENV === 'production' ? 'https://automerchant.vercel.app' : window.location.origin;
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo: redirectUrl, skipBrowserRedirect: false }
      });
      if (error) throw error;
      setView('oauth');
    } catch (err) {
      alert(err.message || 'Failed to sign in with Google. Please try again.');
    }
  };

  const handleLogout = () => {
    setView('landing');
    setUserAlreadySignedUp(false);
    setSignupNumber(null);
    setUserEmail(null);
    setUserApproved(false);
    setIsProcessing(false);
    localStorage.removeItem('authToken');
  };

  if (view === 'product' && userApproved) {
    return <ProductDashboard userEmail={userEmail} onLogout={handleLogout} />;
  }

  if (view === 'success') {
    if (userEmail === ADMIN_EMAIL) {
      return <AdminPanel userEmail={userEmail} onLogout={handleLogout} />;
    }
    return <SuccessPage signupNumber={signupNumber} onLogout={handleLogout} userEmail={userEmail} />;
  }

  if (view === 'error' && error) {
    return (
      <div className="min-h-screen bg-dark-950 flex flex-col items-center justify-center p-4">
        <div className="text-center max-w-lg glass-card-linear p-8 rounded-2xl border border-red-500/30">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Connection Error</h2>
          <p className="text-slate-400 mb-6">{error.message}</p>
          {error.canRetry && (
            <button
              onClick={error.retryAction}
              className="btn-primary-glow px-6 py-3 flex items-center justify-center mx-auto gap-2"
            >
              <RefreshCw className="w-5 h-5" />
              Try Again
            </button>
          )}
        </div>
      </div>
    );
  }

  if (view === 'oauth') {
    return (
      <div className="min-h-screen bg-dark-950 flex flex-col items-center justify-center p-4">
        <div className="text-center">
          <RefreshCw className="w-16 h-16 text-emerald-400 animate-spin mx-auto mb-4" />
          <p className="text-xl text-white">Signing you in...</p>
        </div>
      </div>
    );
  }

  return (
    <LandingPage
      onJoinWaitlist={handleJoinWaitlist}
      waitlistCount={waitlistCount}
      userAlreadySignedUp={userAlreadySignedUp}
    />
  );
}

export default App;
